import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { differenceInDays } from "date-fns";

import {
  performUpdateInventoryMini,
  sanitizeInventoryItem,
} from "../../../../graphql/inventory/inventoryCRUD";

import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { orange } from "@material-ui/core/colors";

import {
  Button,
  Edit,
  FormDataConsumer,
  FormTab,
  Loading,
  maxValue,
  minValue,
  number,
  NumberInput,
  required,
  SaveButton,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
  useRefresh,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  DAMAGED_COLOR,
  DamagedIcon,
  ExceptionIcon,
} from "../../../../components/common/iconography";
import { BackButton } from "../../../../components/common/BackButton";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HelpButton } from "../../../../components/common/HelpButton";
import { HistoryTab } from "../../../../components/common/HistoryTab";
import { LocationPicker } from "../../../../components/common/LocationPicker";
import { ConditionalConfirm } from "../../../../components/common/ConditionalConfirm";

import { logError } from "../../../../utils/appUtils";
import {
  shouldRefreshInventoryItemTrackCar,
  useTrackCarSubscriptionRefresh,
} from "../../../../utils/useTrackCarSubscriptionRefresh";
import {
  InventoryStatus,
  LocationType,
  NoteType,
} from "../../../../utils/constants";
import { getUnitName } from "../../../../utils/loadTypeUtils";
import {
  deepRedirect,
  getBackPath,
  historyAutoGen,
  trimDecimal,
} from "../../../../utils/utils";
import {
  isManagedLocation,
  promiseSetCapacityUsed,
} from "../../../../utils/locationUtils";

import { InventoryItemHelp } from "./InventoryItemHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";
import {
  compareObjects,
  ExceptionDialog,
  isOnHand,
  validateInventoryItemBeforeUpdate,
} from "./InventoryItemUtils";
import { formValidate, getLatestShippedOutboundId } from "../InventoryUitls";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const validateQtyRequired = (min, max) => [
  required(),
  number(),
  minValue(min, `Must be equal/greater than ${min}`),
  maxValue(max, "Exceeds Qty. Advised"),
];
const validateMinQuantity = (min) => [
  required(),
  number(),
  minValue(min, `Must be equal/greater than ${min}`),
];
// Over/under exceptions are crew-managed during inbound processing only.
const disableManagerOverUnderShipment = true;

const validateEditForm = (record, values) => {
  const errors = {};
  if (!record) return errors;

  const priorQty = trimDecimal(Number(record.quantityOnHand), 3);
  const nextQty = trimDecimal(Number(values.quantityOnHand), 3);
  const quantityOnHandChanged =
    Number.isFinite(priorQty) &&
    Number.isFinite(nextQty) &&
    priorQty !== nextQty;

  if (isOnHand(record) && quantityOnHandChanged && !values.otherException) {
    errors.quantityOnHand =
      "Apply 'Other Exception' (with a note) before changing quantity on hand.";
  }

  return errors;
};

const ExceptionsContext = React.createContext();

export const ExceptionsProvider = ({ children, initialExceptions }) => {
  const [exceptions, setExceptions] = useState(initialExceptions || {});

  // Sync exceptions if initialExceptions changes (e.g. after refresh)
  useEffect(() => {
    setExceptions(initialExceptions || {});
  }, [initialExceptions]);

  return (
    <ExceptionsContext.Provider value={{ exceptions, setExceptions }}>
      {children}
    </ExceptionsContext.Provider>
  );
};

const EditActions = ({
  data,
  backPath,
  inventory,
  isQtyDisabled,
  isOverUnderShipmentDisabled,
  showDisabledOverUnderShipment,
  ...props
}) => {
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState();
  const { exceptions, setExceptions } = useContext(ExceptionsContext);

  return (
    <TopToolbar>
      <HelpButton>
        <InventoryItemHelp {...props} />
      </HelpButton>
      <Button
        label="Exception"
        color="primary"
        disabled={inventory?.status !== InventoryStatus.STORED}
        onClick={() => setIsExceptionDialogOpen(true)}
      >
        <ExceptionIcon />
      </Button>
      <BackButton {...props} backPath={backPath} />
      <ExceptionDialog
        {...props}
        record={data}
        inventory={inventory}
        isExceptionDialogOpen={isExceptionDialogOpen}
        setIsExceptionDialogOpen={setIsExceptionDialogOpen}
        exceptions={exceptions}
        setExceptions={setExceptions}
        isQtyDisabled={isQtyDisabled}
        isOverUnderShipmentDisabled={isOverUnderShipmentDisabled}
        showDisabledOverUnderShipment={showDisabledOverUnderShipment}
      />
    </TopToolbar>
  );
};

const EditToolbar = (props) => {
  const classes = useStyles();
  const form = useForm();
  const history = useHistory();
  const refresh = useRefresh();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  const { exceptions } = useContext(ExceptionsContext);
  const { record, inventory, putawayQuantity, backPath, notify } = props;

  const handleDialogConfirm = () => handleSave();
  const handleDialogCloseRefresh = () => {
    debugger;
    setOpen(false);
    refresh();
  };
  const handleDialogClose = () => setOpen(false);
  const [onConfirm, setOnConfirm] = useState(() => handleDialogConfirm);

  const handleValidateSave = async () => {
    const formData = form.getState().values;
    const tempMsg = [];
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
    debugger;

    const isValid = formValidate(form, notify);
    if (!isValid) {
      handleSave(); // trigger field-level validation
      return;
    }

    try {
      setIsSaving(true);
      // Stale inventory validation:
      const { staleErrors } = await validateInventoryItemBeforeUpdate(formData);

      // If any staleErrors: force a refresh via Confirm
      if (staleErrors.length > 0) {
        setConfirmContent(
          <div>
            <p>Data was modified by another user while you were editing:</p>
            <ul>
              {staleErrors.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
            <p>Confirm to refresh and apply your edits again.</p>
          </div>,
        );
        setOnConfirm(() => handleDialogCloseRefresh);
        setOpen(true);
        setIsSaving(false);
        return;
      }
    } catch (err) {
      logError(
        "inventoryItems:DialogSaveButton:validateInventoryItemBeforeUpdate",
        err,
        notify,
      );
      setIsSaving(false);
    }

    const quantityLabel = isOnHand(record) ? "Qty. On Hand" : "Putaway Qty.";
    if (putawayQuantity === 0 && record.status !== InventoryStatus.SHIPPED) {
      tempMsg.push(
        quantityLabel + " set to 0. Item will be removed from inventory.",
      );
    }

    if (tempMsg.length) {
      tempMsg.push("Proceed?");
      const messageFragment = (
        <>
          {tempMsg.map((msg, index) => (
            <React.Fragment key={index}>
              {msg} <br />
            </React.Fragment>
          ))}
        </>
      );
      setConfirmContent(messageFragment);
      setOpen(true);
      setIsSaving(false);
    } else handleSave();
  };

  const handleSave = () => {
    handleDialogClose();
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };
    debugger;
    const updateDateTime = new Date().toISOString();

    data.hasException = data.hasException ? data.hasException : false;
    data.isDamaged = data.hasException ? data.isDamaged : false;
    data.otherException = data.hasException ? data.otherException : false;

    const initStatus = record.status;
    const initQty = record.quantityOnHand;
    const newQty = data.quantityOnHand;

    const ignoreList = [
      "aisleId",
      "areaId",
      "hasException",
      "locationId",
      "outboundShipmentId",
      "quantityOnHand",
      "zoneId",
    ];
    const includeArrayList = [];
    data.history = historyAutoGen(form, data, ignoreList, includeArrayList);

    if (initQty !== newQty) {
      const notes = [];

      // Consolidate status logic
      if (newQty === 0) {
        data.status = InventoryStatus.SHIPPED;
        if (initStatus !== InventoryStatus.SHIPPED) {
          data.depletedAt = updateDateTime;
        }
      } else if (initQty === 0 && newQty > 0) {
        data.status = InventoryStatus.STORED;
        data.depletedAt = null;
      }

      // Only push a status‐note if it actually changed
      if (initStatus !== data.status) {
        notes.push({
          type: NoteType.INVENTORY,
          date: updateDateTime,
          by: window.appUser?.fullName,
          text: `Status set to ${data.status}`,
        });
      }

      // Always push the quantity‐change note
      notes.push({
        type: NoteType.INVENTORY,
        date: updateDateTime,
        by: window.appUser?.fullName,
        text: `Quantity of ${initQty} ${inventory.inventoryUOM} changed to ${newQty}`,
      });

      data.history = [...data.history, ...notes];
    }

    data = sanitizeInventoryItem(data);

    // delete data.inboundShipment;
    // delete data.outboundShipment;
    // delete data.reloadYardCommodity;

    delete data.overShipment;
    delete data.underShipment;

    return data;
  };

  const onSuccess = async ({ data }) => {
    try {
      debugger;
      // const initQty = do not use: formState.initialValues.quantityOnHand;
      const initStatus = record.status;
      const initQty = record.quantityOnHand;
      const newQty = data.quantityOnHand;

      const notes = [];
      const promiseList = [];

      // Start from the current inventory; locationId cleared as before
      const inventoryToUpdate = { ...inventory, locationId: null };

      // Compute the total across all items AFTER this edit (used in multiple places below)
      const items = inventoryToUpdate.inventoryItems?.items ?? [];
      const totalAfterEdit = items.reduce((total, item) => {
        const q =
          item.id === data.id ? Number(newQty) : Number(item.quantityOnHand);
        return total + (Number.isFinite(q) ? q : 0);
      }, 0);
      const initTotal = trimDecimal(inventoryToUpdate.quantityOnHand, 3);
      const newTotal = trimDecimal(totalAfterEdit, 3);

      // A bulk tank holds one pool in one condition, so the moment stock already
      // sitting in the tank is marked damaged, the tank itself is contaminated.
      // Nothing else stamps it on this path: the crew screens stamp on placement
      // and on relocation, but a manager marking placed stock damaged is a
      // condition change with no movement behind it. Left unstamped the tank
      // still reads clean, the next clean load passes canStoreInBulkTank, and it
      // merges into the damaged pool -- which for an indivisible commodity is the
      // contamination the rule exists to prevent.
      //
      // Only ever set, never cleared here: per canStoreInBulkTank a tank comes
      // clean again by being emptied, not by a manager un-ticking the exception
      // on the bookkeeping row.
      //
      // Computed before the capacity block below so a save that also changes
      // quantity carries the flag in that same updateLocation, instead of racing
      // a second write against it.
      const contaminatesTank =
        data.location?.type === LocationType.BULK &&
        Boolean(data.isDamaged) &&
        !data.location.isContaminated;
      if (contaminatesTank) data.location.isContaminated = true;
      let isLocationPersisted = false;

      if (initTotal !== newTotal) {
        const delta = trimDecimal(initQty - newQty, 3);

        // update inventory qty rollups
        if (
          data.status !== InventoryStatus.IN_TRANSIT &&
          data.status !== InventoryStatus.ARRIVED
        ) {
          // qty onHand only updated if STORED / AGGREGATED / SHIPPED,
          // otherwise (e.g., UNLOADED) update qty delivered
          if (
            data.status === InventoryStatus.STORED ||
            data.status === InventoryStatus.AGGREGATED ||
            data.status === InventoryStatus.SHIPPED
          ) {
            inventoryToUpdate.quantityOnHand = newTotal;
          } else {
            inventoryToUpdate.quantityReceived = newTotal;
          }
        }

        // status transitions driven by onHand
        if (
          inventoryToUpdate.quantityOnHand <= 0 &&
          inventoryToUpdate.status === InventoryStatus.STORED
        ) {
          // mark inventory as SHIPPED
          inventoryToUpdate.status = InventoryStatus.SHIPPED;
          inventoryToUpdate.depletedAt = data.updatedAt;
          inventoryToUpdate.lastOutboundId =
            getLatestShippedOutboundId(inventoryToUpdate);
          inventoryToUpdate.turnDays = differenceInDays(
            new Date(data.updatedAt),
            new Date(inventoryToUpdate.receivedAt),
          );
        } else if (
          inventoryToUpdate.quantityOnHand > 0 &&
          inventoryToUpdate.status === InventoryStatus.SHIPPED
        ) {
          inventoryToUpdate.status = InventoryStatus.STORED;
          inventoryToUpdate.depletedAt = null;
          inventoryToUpdate.lastOutboundId = null;
          inventoryToUpdate.turnDays = null;
        }

        if (initStatus !== inventoryToUpdate.status) {
          notes.push({
            type: NoteType.INVENTORY,
            date: data.updatedAt,
            by: window.appUser?.fullName,
            text: `Status set to ${inventoryToUpdate.status}`,
          });
        }

        notes.push({
          type: NoteType.INVENTORY,
          date: data.updatedAt,
          by: window.appUser?.fullName,
          text: `Quantity of ${initQty} ${inventoryToUpdate.inventoryUOM} changed to ${newQty} in ${data.locationFullName}`,
        });

        // update capacity used
        const location = data.location;
        if (
          data.status === InventoryStatus.IN_TRANSIT ||
          data.status === InventoryStatus.ARRIVED
        ) {
          // if inventoryItem is not UNLOADED / STORED update capacityAllocated
          location.capacityAllocated = trimDecimal(
            location.capacityAllocated - delta,
            3,
          );
        } else {
          // update capacityUsed
          location.capacityUsed = trimDecimal(location.capacityUsed - delta, 3);
        }

        promiseList.push(
          promiseSetCapacityUsed(
            location,
            location.capacityUsed,
            location.capacityAllocated,
          ),
        );
        isLocationPersisted = true;
      }

      // The block above persists the location whenever it moves capacity. A
      // damage-only save moves none, so the stamp needs a write of its own.
      if (contaminatesTank && !isLocationPersisted) {
        promiseList.push(promiseSetCapacityUsed(data.location));
      }

      // ------------------- DAMAGE + OTHER ------------------- //
      if (!inventory.isDamaged && exceptions.isDamaged) {
        inventoryToUpdate.isDamaged = true;
      }
      if (!inventory.otherException && exceptions.otherException) {
        inventoryToUpdate.otherException = true;
        if (!inventory.otherExceptionNote && exceptions.otherExceptionNote) {
          inventoryToUpdate.otherExceptionNote = exceptions.otherExceptionNote;
        }
      }

      // ------------------- HAS EXCEPTION ------------------- //
      inventoryToUpdate.hasException =
        inventoryToUpdate.overShipment ||
        inventoryToUpdate.underShipment ||
        inventoryToUpdate.isDamaged ||
        inventoryToUpdate.otherException ||
        inventory.isRecalled;

      inventoryToUpdate.history = [...inventoryToUpdate.history, ...notes];
      promiseList.push(performUpdateInventoryMini(inventoryToUpdate));

      await Promise.all(promiseList);

      notify("Inventory Item updated successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError("inventoryItems:EditToolbar:onSuccess", error, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("inventoryItems:EditToolbar:onFailure", error, notify);
  };

  const isPristine = () => {
    const formState = form.getState();
    return formState.pristine;
  };

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        label="Save"
        disabled={isPristine() || isDeleting}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={confirmContent}
        onConfirm={onConfirm}
        onClose={handleDialogClose}
      />
      <DeepDeleteButton
        {...props}
        isSaving={isSaving}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
        inventory={inventory}
        inventoryItems={inventory.inventoryItems}
      />
    </Toolbar>
  );
};

export const InventoryItemEdit = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { record } = useEditController(props);
  const inventory = record?.inventory || props.location?.state?.inventory || {};

  const reloadYardId = window.appUser?.reloadYardId;
  const currentInboundId =
    record?.inboundShipmentId || record?.inventory?.inboundShipmentId;
  const currentOutboundId =
    record?.outboundShipmentId || record?.inventory?.outboundShipmentId;

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(reloadYardId && record?.id),
    label: "InventoryItemEdit",
    shouldRefresh: (trackCar) => {
      return shouldRefreshInventoryItemTrackCar({
        trackCar,
        reloadYardId,
        currentInboundId,
        currentOutboundId,
        recordId: record?.id,
      });
    },
    onRefresh: () => {
      setTimeout(() => refresh(), 2500);
      setTimeout(() => refresh(), 6000);
    },
  });

  const [putawayQuantity, setPutawayQuantity] = useState(
    record?.quantityOnHand,
  );

  const location = record?.location;
  const isQtyDisabled =
    location?.type === LocationType.STRUCTURED &&
    !location?.locationLoad?.other.selected;

  const initialExceptions = useMemo(
    () => ({
      overShipment: record?.inventory?.overShipment,
      overShipmentQuantity: record?.inventory?.overShipmentQuantity,
      overShipmentNote: record?.inventory?.overShipmentNote,
      underShipment: record?.inventory?.underShipment,
      underShipmentQuantity: record?.inventory?.underShipmentQuantity,
      underShipmentNote: record?.inventory?.underShipmentNote,
      hasException: record?.hasException,
      isDamaged: record?.isDamaged,
      originOfDamage: record?.originOfDamage,
      otherException: record?.otherException,
      otherExceptionNote: record?.otherExceptionNote,
    }),
    [record],
  );

  const backPath = getBackPath(props);

  const initQtyAllocated = inventory.inventoryItems?.items?.reduce(
    (total, item) => {
      return total + item.quantityOnHand;
    },
    0,
  );

  const maxPutawayQty =
    initQtyAllocated <= inventory.quantityAdvised
      ? inventory.quantityAdvised - initQtyAllocated + record?.quantityOnHand
      : 0;

  const minPutawayQty =
    record?.quantityCommitted > 0
      ? trimDecimal(record?.quantityOnHand - record?.quantityCommitted, 3)
      : 0;

  useEffect(() => {
    if (record) {
      debugger;
      setPutawayQuantity(record.quantityOnHand);
    }
  }, [record]);

  if (
    !record ||
    !inventory.inventoryItems ||
    !inventory.reloadYardCommodity?.commodity
  )
    return <Loading />;

  return (
    <ExceptionsProvider initialExceptions={initialExceptions}>
      <Edit
        {...props}
        title={<ForceTitle title={`Edit Inventory Item`} />}
        undoable={false}
        actions={
          <EditActions
            {...props}
            data={record}
            backPath={backPath}
            inventory={inventory}
            isQtyDisabled={isQtyDisabled}
            isOverUnderShipmentDisabled={disableManagerOverUnderShipment}
            showDisabledOverUnderShipment={true}
          />
        }
      >
        <TabbedForm
          variant="outlined"
          submitOnEnter={false}
          enableReinitialize={true} // STAFF LEVEL FIX: Ensure form resets when record is refreshed via subscriptions
          toolbar={
            <EditToolbar
              {...props}
              record={record}
              inventory={inventory}
              putawayQuantity={putawayQuantity}
              maxPutawayQty={maxPutawayQty}
              backPath={backPath}
              notify={notify}
            />
          }
          validate={(allValues) => validateEditForm(record, allValues)}
        >
          <FormTab label="Location">
            <InventoryItemForm
              {...props}
              record={record}
              inventory={inventory}
              putawayQuantity={putawayQuantity}
              setPutawayQuantity={setPutawayQuantity}
              maxPutawayQty={maxPutawayQty}
              minPutawayQty={minPutawayQty}
              initialExceptions={initialExceptions}
              notify={notify}
            />
          </FormTab>
          <FormTab label="History Log">
            <HistoryTab />
          </FormTab>
        </TabbedForm>
      </Edit>
    </ExceptionsProvider>
  );
};

const isQtyOnHandDisabled = (formData, exceptions) => {
  if (exceptions?.otherException) {
    return false; // enabled regardless of anything else
  }

  if (isManagedLocation(formData?.location)) {
    return true; // disabled
  } else if (
    formData?.status === InventoryStatus.ARCHIVED ||
    formData?.status === InventoryStatus.SHIPPED
  ) {
    return true; // lock editing after SHIPPED, enable with Other Exception
  }

  return false; // default: enabled
};

const InventoryItemForm = ({
  backPath,
  record,
  inventory,
  putawayQuantity,
  setPutawayQuantity,
  maxPutawayQty,
  minPutawayQty,
  initialExceptions,
  notify,
  ...props
}) => {
  const form = useForm();
  const [originalLocation, setOriginalLocation] = useState();
  const [selectedLocation, setSelectedLocation] = useState();
  const [bayList, setBayList] = useState([]);
  const isMaxQuantity = putawayQuantity === maxPutawayQty;

  const { exceptions } = useContext(ExceptionsContext);

  useEffect(() => {
    if (record.location) {
      setOriginalLocation(record.location);
      setSelectedLocation(record.location);
    }
  }, [record?.location]);

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "hasException",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "isDamaged",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "originOfDamage",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "overShipment",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "underShipment",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "otherException",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "otherExceptionNote",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );

  useEffect(() => {
    debugger;
    const isEqual = compareObjects(initialExceptions, exceptions);

    if (!isEqual) {
      form.change("hasException", exceptions?.hasException);
      form.change("isDamaged", exceptions?.isDamaged);
      form.change("originOfDamage", exceptions?.originOfDamage);
      form.change("otherException", exceptions?.otherException);
      form.change("otherExceptionNote", exceptions?.otherExceptionNote);
    }

    // Trigger quantity validation after exception changes that affect quantity rules.
    const putawayQuantity = form.getFieldState("quantityOnHand").value;
    if (putawayQuantity !== undefined && putawayQuantity !== null) {
      form.change("quantityOnHand", putawayQuantity - 1);
      form.change("quantityOnHand", putawayQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptions]);

  const handleLocation = () => {};

  const handlePutawayQuantity = (event) => {
    const raw = event?.target?.value ?? 0;
    const newPutawayQuantity = Number(raw);
    setPutawayQuantity(newPutawayQuantity);
  };

  const getQuantityValidator = () => {
    if (exceptions.otherException) {
      return validateMinQuantity(record?.quantityCommitted);
    }
    return validateQtyRequired(minPutawayQty, maxPutawayQty);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextInput
                source="inventory.productName"
                label="Product Name"
                variant="outlined"
                fullWidth
                disabled={true}
              />
            </Grid>
            {!isOnHand(record) && (
              <Grid item xs={12} sm={3}>
                <TextField
                  id="avaialble-quantity"
                  label="Quantity Avaialble"
                  variant="outlined"
                  margin="dense"
                  disabled={true}
                  value={maxPutawayQty}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {" "}
                        {getUnitName(inventory.inventoryUOM)}{" "}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={1}>
            <Grid item container xs={12} sm={6} spacing={1}>
              <Grid item xs={12} sm={6}>
                <NumberInput
                  source="quantityOnHand"
                  label={
                    isOnHand(record) ? "Quantity On Hand" : "Putaway Quantity"
                  }
                  variant="outlined"
                  fullWidth
                  min={minPutawayQty}
                  max={exceptions.overShipment ? undefined : maxPutawayQty}
                  validate={getQuantityValidator()}
                  disabled={isQtyOnHandDisabled(formData, exceptions)}
                  onChange={handlePutawayQuantity}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {" "}
                        {getUnitName(inventory.inventoryUOM)}{" "}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {isOnHand(record) && (
                <Grid item xs={12} sm={6}>
                  <NumberInput
                    source="quantityCommitted"
                    label="Quantity Committed"
                    variant="outlined"
                    fullWidth
                    disabled={true}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {" "}
                          {getUnitName(record.inventory.inventoryUOM)}{" "}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
            </Grid>
            {exceptions.overShipment && (
              <Grid container item xs={12} sm={2} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      arrow
                      placement="left"
                      title={
                        exceptions.overShipmentQuantity
                          ? `Qty. Over: ${
                              exceptions.overShipmentQuantity || ""
                            }`
                          : "Overshipment"
                      }
                    >
                      <ExceptionIcon
                        fontSize="large"
                        style={{ color: orange[600] }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">
                      Overshipment
                      {exceptions.overShipmentQuantity
                        ? `: ${exceptions.overShipmentQuantity}`
                        : ""}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {exceptions.underShipment && (
              <Grid container item xs={12} sm={2} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      arrow
                      placement="left"
                      title={
                        exceptions.underShipmentQuantity
                          ? `Qty. Under: ${
                              exceptions.underShipmentQuantity || ""
                            }`
                          : "Undershipment"
                      }
                    >
                      <ExceptionIcon
                        fontSize="large"
                        style={{ color: orange[600] }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">
                      Undershipment
                      {exceptions.underShipmentQuantity
                        ? `: ${exceptions.underShipmentQuantity}`
                        : ""}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {formData.isDamaged && (
              <Grid container item xs={12} sm={2} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      title={
                        formData.originOfDamage
                          ? formData.originOfDamage
                          : "Damaged"
                      }
                      placement="left"
                      arrow
                    >
                      <DamagedIcon
                        fontSize="large"
                        style={{ color: DAMAGED_COLOR }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">Damaged</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {formData.otherException && (
              <Grid container item xs={12} sm={2} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      title={
                        formData.otherExceptionNote
                          ? formData.otherExceptionNote
                          : "Other Exception"
                      }
                      placement="left"
                      arrow
                    >
                      <ExceptionIcon
                        fontSize="large"
                        style={{ color: orange[600] }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">Other Exception</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Location:
              </Typography>
              <br />
            </Grid>
            <Grid item xs={12}>
              <LocationPicker
                {...props}
                inventory={inventory}
                inventoryItem={formData}
                originalLocation={originalLocation}
                isMaxQuantity={isMaxQuantity}
                required={true}
                disabled={true}
                bayList={bayList}
                setBayList={setBayList}
                handleLocation={handleLocation}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
              />
            </Grid>
          </Grid>
        </>
      )}
    </FormDataConsumer>
  );
};
